import React, { useEffect, useState, useMemo } from "react";

import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_TablePagination,
} from "material-react-table";

import * as moment from "moment";
import axios from "../../axios";
import { NavLink } from "react-router-dom";
import { Box } from "@mui/material";

const PatientsTable = (props) => {
  const startDate = props.startDate;
  const endDate = props.endDate;
  const darkTheme = createTheme({
    palette: {
      mode: "light",
    },
  });

  const [initialRender, setIntialRender] = useState(true);
  const [firstSortedCall, setFirstSortedCall] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [prevPageIndex, setPrevPageIndex] = useState(1);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });
  const [pageNumber, setPageNumber] = useState(1);
  const [data, setData] = useState(props.propData);
  const [pageSize, setPageSize] = useState(50);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorKey: "patientId",
        header: "Patient ID",
        enableSorting: true,
        enableColumnFilterModes: false,
        Cell: ({ cell }) => (
          <NavLink
            className="table__row__item"
            target="_blank"
            to={`patient/${cell.row.original.userId}/${encodeURIComponent(cell.getValue())}`}
            // to={`patientDetails/${cell.row.original.userId}/${cell.getValue()}`}
            // to={`patientDetails/${localStorage.doctorId}/${cell.getValue()}/${cell.row.original.id}`}
          >
            {cell.getValue() || "-"}
          </NavLink>
        ),
      },
      {
        accessorKey: "firstname", //access nested data with dot notation
        header: "Name",
        enableSorting: true,
        size: 180,
        enableColumnFilterModes: false,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "total_assessments",
        header: "Assessments",
        enableSorting: true,
        enableColumnFilter: false,
        enableColumnFilterModes: false,
        size: 180,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "location", //normal accessorKey
        header: "Location",
        enableSorting: true,
        enableColumnFilter: false,
        enableColumnFilterModes: false,
        size: 180,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "mobileNumber",
        header: "Mobile Number",
        enableSorting: false,
        size: 190,
        enableColumnFilterModes: false,
        Cell: ({ cell }) => {
          const mobileNumber = cell.getValue();
          return mobileNumber ? `**** **** ${mobileNumber.slice(-4)}` : "-";
        },
      },
      {
        accessorKey: "gender",
        header: "Gender",
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnFilterModes: false,
        size: 180,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "createdOn",
        header: "Enrollment Date",
        enableSorting: true,
        enableColumnFilter: false,
        enableColumnFilterModes: false,
        size: 190,
        Cell: ({ cell }) => {
          return (
            <span>
              {cell.getValue()
                ? moment(cell.row.original.createdOn).format("YYYY-MM-DD")
                : "-"}
            </span>
          );
        },
      },
    ];
    if (
      (localStorage.validation === "true" &&
        localStorage.sharedPatient === "true") ||
      localStorage.role === "admin"
    ) {
      baseColumns.push({
        accessorKey: "userId",
        header: "Doctor Id",
        enableSorting: false,
        enableColumnFilter: true,
        size: 250,
      });
    }

    if (localStorage.getItem("validation") === "true") {
      baseColumns.push({
        accessorKey: "label",
        header: "Labeled",
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnFilterModes: false,
        size: 160,
        Cell: ({ cell }) => {
          const records = cell.row.original?.records;
          const isLabelled = records.some((record) => record.label !== null);
          return isLabelled ? "Yes" : "No";
        },
      });
    }
    return baseColumns;
  });

  const table = useMaterialReactTable({
    columns,
    muiPaginationProps: {
      rowsPerPageOptions: [5, 20, 50, 100],
      showFirstButton: false,
      showLastButton: false,
    },

    muiTableProps: {
      sx: {
        borderCollapse: "collapse",
        "& td": {
          border: "none",
          fontSize: "14px",
        },
        "& th": {
          borderRight: "2px solid #a9a9a9",
          // borderLeft: '1px solid #606060',
          // margin:'2px 0 2px 0'
        },
      },
    },
    muiSkeletonProps: {},

    muiTableHeadCellProps: {
      sx: {
        display: "flex",
        justifyContent: "center",
        fontSize: "14px",
        backgroundColor: "#fdfaf0",
        minHeight: "40px",
        borderRight: "1px solid #000",
      },
      // '& .Mui-TableHeadCell-Content': {
      //   // fontSize: '24px',
      // },
    },
    muiTableBodyCellProps: {
      sx: {
        fontSize: "12px",
      },
    },
    muiTopToolbarProps: {
      sx: {
        boxSizing: "12px",
        fontSize: "14px",
      },
    },
    muiToolbarAlertBannerProps: isError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    muiTableContainerProps: { sx: { maxHeight: "600px" } },
    data,
    manualFiltering: true,
    manualSorting: true,
    enableGlobalFilter: false,
    enableSortingRemoval: true,
    enableDensityToggle: true,
    manualGrouping: true,
    pageSize: pageSize,
    manualPagination: true,
    enableFilterMatchHighlighting: false,
    // enableStickyHeader: true,
    // enableStickyFooter: true,
    // enableFilterMatchHighlighting: true,

    columnFilterDisplayMode: "popover",
    enableColumnFilterModes: true,
    enableFacetedValues: true,
    enableFilters: true,

    sorting: sorting,
    onSortingChange: setSorting,
    // getFacetedUniqueValues: (table) => {
    //   const uniqueValueMap = new Map<string, number>()
    //   //fetch unique values from server, ideally including the count of each unique value
    //   return uniqueValueMap;
    // },
    enableColumnOrdering: true,
    enableColumnResizing: true,

    initialState: { showColumnFilters: true },
    renderEmptyRowsFallback: ({ table }) => (
      <tr>
        <td
          colSpan={columns.length}
          style={{
            textAlign: "center",
            display: "flex",
            fontWeight: "400",
            paddingTop: "0.5em",
            paddingBottom: "1em",
            fontSize: "1em",
            lineHeight: "1.5",
            justifyContent: "center",
            alignItems: "center",
            color: "rgba(0, 0, 0, 0.6)",
            fontStyle: "italic",
          }}
        >
          No Records found. Please try with different date filter.
        </td>
      </tr>
    ),

    renderBottomToolbar: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingLeft: "10px",
          // fontSize:'30px',
          // fontWeight: 'bold'
        }}
      >
        <MRT_TablePagination table={table} />
      </Box>
    ),

    isMultiSortEvent: () => false,

    onColumnFiltersChange: setColumnFilters,
    // onColumnFiltersChange: handleFilterChange,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    rowCount,
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      sorting,
    },
  });

  const queryParams = columnFilters.reduce((params, filter) => {
    params[filter.id] = filter.value;
    return params;
  }, {});
  
  const sortingParams = (sorting) => {
    const params = {};

    if (sorting && sorting.length > 0) {
      const sortInfo = sorting[0];
      params["sort_field"] = sortInfo.id;
      params["sort_type"] = sortInfo.desc ? "desc" : "asc";
    }
    return params;
  };

  const fetchData = async () => {
    if (!data.length) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }
    const startDate1 = moment(startDate).format("YYYY-MM-DD");
    const endDate1 = moment(endDate).format("YYYY-MM-DD");
    try {
      const response = await axios.get(
        `/getPatientsAssessments?created_on_start=${startDate1} 00:00:00&created_on_end=${endDate1} 23:59:59`,
        {
          headers: { accessToken: localStorage.accessToken },
          params: {
            page_size: null,
            pageNumber: pageNumber,
          },
        }
      );

      if (response.data.message) {
        setData([]);
        setRowCount(0)
      } else {
        console.log(response.data.data)
        // console.log("patients", response.data.data.patients);
        setData(response.data.data.patients);
        setRowCount(response.data.data.total_patients);
      }
    } catch (error) {
      setIsError(true);
      console.error(error);
      return;
    }

    setIsError(false);
    setIsLoading(false);
    setIsRefetching(false);
  };

  useEffect(() => {
    if (initialRender) {
      setIntialRender(false);
      fetchData();
    } else {
      fetchData();
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!initialRender) {
      fetchPaginatedData();
    }
  }, [pagination.pageIndex]);

  useEffect(() => {
    if (!initialRender) {
      fetchPageSizeData();
    }
  }, [pagination.pageSize]);

  useEffect(() => {
    if (!initialRender) {
      fetchSortedData();
    }
  }, [sorting]);

  useEffect(() => {
    if (!initialRender) {
      fetchFilteredData();
      console.log(columnFilters);
    }
  }, [columnFilters]);

  const fetchPaginatedData = async () => {
    const nextPageIndex = pagination.pageIndex + 1;

    setIsLoading(true);

    try {
      if (nextPageIndex > pagination.pageIndex) {
        console.log("next");
        await fetchNextpatients(nextPageIndex);
      } else if (nextPageIndex < pagination.pageIndex) {
        console.log("prev");
        await fetchPreviouspatients(nextPageIndex);
      }
    } catch (error) {
      console.error("Error fetching paginated data:", error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPageSizeData = async () => {
    if (!data.length) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }
    const startDate1 = moment(startDate).format("YYYY-MM-DD");
    const endDate1 = moment(endDate).format("YYYY-MM-DD");
    try {
      const response = await axios.get(
        `/getPatientsAssessments?created_on_start=${startDate1} 00:00:00&created_on_end=${endDate1} 23:59:59`,
        {
          headers: { accessToken: localStorage.accessToken },
          params: {
            page_size: pagination.pageSize,
          },
        }
      );
      if (response.data.message) {
        setData([]);
        // console.log("no data");
      } else {
        setData(response.data.data.patients);
        console.log("patients list", response.data.data.patients);
        if (firstSortedCall) {
          setFirstSortedCall(false);
        }
        setRowCount(response.data.data.total_patients);
      }
    } catch (error) {
      setIsError(true);
      console.error(error);
      return;
    }

    setIsError(false);
    setIsLoading(false);
    setIsRefetching(false);
  };

  const fetchSortedData = async () => {
    const startDate1 = moment(startDate).format("YYYY-MM-DD");
    const endDate1 = moment(endDate).format("YYYY-MM-DD");
    if (!data.length) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }
    await axios
      .get(
        `/getPatientsAssessments?created_on_start=${startDate1} 00:00:00&created_on_end=${endDate1} 23:59:59`,
        {
          headers: { accessToken: localStorage.accessToken },
          params: {
            page_size: pagination.pageSize,
            ...sortingParams(sorting),
            ...queryParams,
          },
        }
      )
      .then((response) => {
        setData(response.data.data.patients);
        setRowCount(response.data.data.total_patients);
      })
      .catch((error) => {
        console.log("error", error);
      });
    setIsError(false);
    setIsLoading(false);
    setIsRefetching(false);
  };

  const fetchFilteredData = async () => {
    const startDate1 = moment(startDate).format("YYYY-MM-DD");
    const endDate1 = moment(endDate).format("YYYY-MM-DD");
    if (!data.length) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }
    try {
      const response = await axios.get(`/getPatientsAssessments`, {
        headers: { accessToken: localStorage.accessToken },
        params: {
          created_on_start: `${startDate1} 00:00:00`,
          created_on_end: `${endDate1} 23:59:59`,
          ...queryParams, 
          page_size: pagination.pageSize,
          ...sortingParams(sorting),
        },
      });

      if (response.data.message) {
        setData([]);
      } else {
        setData(response.data.data.patients);
        setRowCount(response.data.data.total_patients);
      }
      setIsError(false);
      setIsLoading(false);
      setIsRefetching(false);
    } catch (error) {
      console.log("error", error);
      setIsError(true);
      setIsLoading(false);
      setIsRefetching(false);
    }
  };

  const fetchNextpatients = async (ind) => {
    const startDate1 = moment(startDate).format("YYYY-MM-DD");
    const endDate1 = moment(endDate).format("YYYY-MM-DD");
    if (!data.length) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }
    try {
      const response = await axios.get(
        `/getPatientsAssessments?created_on_start=${startDate1} 00:00:00&created_on_end=${endDate1} 23:59:59`,
        {
          headers: { accessToken: localStorage.accessToken },
          params: {
            page_number: ind,
            page_size: pagination.pageSize,
            ...queryParams,
            ...sortingParams(sorting),
            
          },
        }
      );
    
      if (response.data.message) {
        setData([]);
      } else {
        setData(response.data.data.patients);


      }
      setIsError(false);
      setIsLoading(false);
      setIsRefetching(false);
    } catch (error) {
      console.log("error");
    }
  };

  const fetchPreviouspatients = async (ind) => {
    setPageNumber((prevState) => prevState - 1);
    setPrevPageIndex((prevState) => prevState - 1);
    const startDate1 = moment(startDate).format("YYYY-MM-DD");
    const endDate1 = moment(endDate).format("YYYY-MM-DD");
    if (!data.length || ind <= 1) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }
    try {
      const response = await axios.get(
        `/getPatientsAssessments?created_on_start=${startDate1} 00:00:00&created_on_end=${endDate1} 23:59:59`,
        {
          headers: { accessToken: localStorage.accessToken },
          params: {
            page_number: ind,
            page_size: pagination.pageSize,
            ...queryParams,
            ...sortingParams(sorting),
          },
        }
      );
      if (response.data.message) {
        setData([]);
      } else {
        setData(response.data.data.patients);

      }
      setIsError(false);
      setIsLoading(false);
      setIsRefetching(false);
    } catch (error) {
      console.log("error");
    }
  };

  return (
    <div>
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <MaterialReactTable table={table} pageSize={pagination.pageSize} />
      </ThemeProvider>
    </div>
  );
};

export default PatientsTable;
